import {
  DndContext, DragOverlay, closestCenter,
  KeyboardSensor, PointerSensor,
  useSensor, useSensors,
  type DragEndEvent, type DragStartEvent,
} from "@dnd-kit/core";
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable";
import { useState } from "react";
import { GripVertical } from "lucide-react";
import type { WidgetKey, LayoutSnapshot } from "./types";
import GlobalDashboardGrid from "./GlobalDashboardGrid";
import useTranslation from "../../client/hooks/useTranslation";

export interface DashboardLayoutProps {
  layout: LayoutSnapshot;
  isEditMode: boolean;
  isMobile: boolean;
  onAddWidgetClick: (colStart: number, rowStart: number, maxColSpan: number) => void;
  onRemoveWidget: (slotId: string) => void;
  onWidgetSizeChange: (slotId: string, colSpan: number, rowSpan: number) => void;
  onMoveWidget: (slotId: string, colStart: number, rowStart: number) => void;
  onSwapWidgets: (slotIdA: string, slotIdB: string) => void;
  onUpdateSlotConfig?: (slotId: string, value: string) => void;
  onUpdateSlotViewMode?: (slotId: string, mode: "chart" | "table") => void;
}

function WidgetDragPreview({ label }: { label: string }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 bg-background border-2 border-primary/60 rounded-lg shadow-xl">
      <GripVertical className="h-4 w-4 text-muted-foreground" />
      <span className="text-sm font-medium">{label}</span>
    </div>
  );
}

function camelize(key: string): string {
  return key.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

export default function DashboardLayout({
  layout, isEditMode, isMobile,
  onAddWidgetClick, onRemoveWidget, onWidgetSizeChange, onMoveWidget, onSwapWidgets,
  onUpdateSlotConfig, onUpdateSlotViewMode,
}: DashboardLayoutProps) {
  const t = useTranslation();
  const [activeSlot, setActiveSlot] = useState<{ slotId: string; widgetKey: WidgetKey } | null>(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragStart(e: DragStartEvent) {
    const id = String(e.active.id);
    if (id.startsWith("widget|")) {
      const slotId = id.slice("widget|".length);
      const slot = layout.slots.find((s) => s.id === slotId);
      if (slot) setActiveSlot({ slotId, widgetKey: slot.widgetKey });
    }
  }

  function handleDragEnd(e: DragEndEvent) {
    setActiveSlot(null);
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const activeId = String(active.id);
    const overId = String(over.id);

    if (!activeId.startsWith("widget|")) return;
    const sourceSlotId = activeId.slice("widget|".length);

    if (overId.startsWith("widget|")) {
      const targetSlotId = overId.slice("widget|".length);
      if (sourceSlotId !== targetSlotId) onSwapWidgets(sourceSlotId, targetSlotId);
      return;
    }

    if (overId.startsWith("empty|")) {
      const parts = overId.split("|");
      const col = Number(parts[1]);
      const row = Number(parts[2]);
      if (!Number.isNaN(col) && !Number.isNaN(row)) onMoveWidget(sourceSlotId, col, row);
    }
  }

  if (!isEditMode || isMobile) {
    return (
      <GlobalDashboardGrid
        layout={layout}
        isEditMode={isEditMode}
        isMobile={isMobile}
        enableDnd={false}
        onAddWidgetClick={onAddWidgetClick}
        onRemoveWidget={onRemoveWidget}
        onWidgetSizeChange={onWidgetSizeChange}
        onMoveWidget={onMoveWidget}
        onSwapWidgets={onSwapWidgets}
        onUpdateSlotConfig={onUpdateSlotConfig}
        onUpdateSlotViewMode={onUpdateSlotViewMode}
      />
    );
  }

  const activeWidgetLabel = activeSlot
    ? (t.dashboard.widgets[camelize(activeSlot.widgetKey) as keyof typeof t.dashboard.widgets] ?? activeSlot.widgetKey)
    : "";

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveSlot(null)}
    >
      <GlobalDashboardGrid
        layout={layout}
        isEditMode={isEditMode}
        isMobile={isMobile}
        enableDnd={true}
        onAddWidgetClick={onAddWidgetClick}
        onRemoveWidget={onRemoveWidget}
        onWidgetSizeChange={onWidgetSizeChange}
        onMoveWidget={onMoveWidget}
        onSwapWidgets={onSwapWidgets}
        onUpdateSlotConfig={onUpdateSlotConfig}
        onUpdateSlotViewMode={onUpdateSlotViewMode}
      />
      <DragOverlay>
        {activeSlot ? <WidgetDragPreview label={activeWidgetLabel} /> : null}
      </DragOverlay>
    </DndContext>
  );
}
